import {
  MAX_QUEUE_ITEMS,
  nextBackoff,
  sameDocument,
  SyncQueueFullError,
  type CapacityAlert,
  type RecipientStatus,
  type BroadcastManifest,
  type BroadcastMessage,
  type BroadcastReceipt,
  type Gym,
  type IdentityMaterial,
  type MemberSchedule,
  type OccupancyDigest,
  type SlotProjection,
  type SyncQueueItem,
} from "@palestra/core";
import { openDB, type DBSchema, type IDBPDatabase, type IDBPObjectStore } from "idb";
import { simStorageSuffix } from "./dev-session";

/**
 * Fuori dalla simulazione il suffisso è vuoto e il nome resta quello di
 * sempre; in sviluppo, con `?sim=<id>`, ogni sessione ha il suo database e
 * quindi la sua identità, il suo `deviceId` e la sua coda (`dev-session.ts`).
 */
const DATABASE_NAME = `palestra-local-first${simStorageSuffix()}`;
const DATABASE_VERSION = 3;

export interface PublicGymDocument {
  schemaVersion: Gym["schemaVersion"];
  id: string;
  name: string;
  timeZone: string;
  /** Facoltativo: i documenti scritti prima della durata globale non ce l'hanno. */
  slotDurationMinutes?: Gym["slotDurationMinutes"];
}

export interface AdminGymDocument {
  schemaVersion: Gym["schemaVersion"];
  gymId: string;
  slots: Gym["slots"];
  /** Facoltativo: i documenti scritti prima delle lapidi non ne hanno. */
  retiredSlots?: Gym["retiredSlots"];
  exceptions: Gym["exceptions"];
  updatedAt: string;
  updatedBy: string;
}

export interface CoachProjectionDocument {
  schemaVersion: 1;
  gymId: string;
  projections: SlotProjection[];
  alerts: CapacityAlert[];
  generatedAt: string;
}

export interface StoredBroadcastDocument {
  message: BroadcastMessage;
  sourceEnvelopeId: string;
  publishedAt?: string;
}

export interface BroadcastReceiptProjection {
  gymId: string;
  broadcastId: string;
  memberId: string;
  receivedAt?: string;
  readAt?: string;
  firstObservedAt?: string;
}

function receiptTime(
  receipt: BroadcastReceipt,
  broadcast: StoredBroadcastDocument | undefined,
  observedAt: string,
): string {
  const occurred = Date.parse(receipt.occurredAt);
  const created = broadcast ? Date.parse(broadcast.message.createdAt) : undefined;
  return Number.isFinite(occurred) &&
    occurred <= Date.parse(observedAt) + 5 * 60 * 1000 &&
    (created === undefined || occurred >= created - 5 * 60 * 1000)
    ? receipt.occurredAt
    : observedAt;
}

interface MetaRecord {
  key: string;
  value: unknown;
}

interface PalestraDatabase extends DBSchema {
  /**
   * Il documento è l'oggetto stesso, non un blocco di byte da decodificare:
   * IndexedDB lo conserva con lo structured clone del browser. Fino allo schema
   * 2 era un documento Automerge serializzato, e leggerlo costava 3,38 MB di
   * WASM per un CRDT che l'applicazione non usava — la convergenza è in
   * `packages/core/src/merge.ts`, ed è codice proprio.
   */
  documents: { key: string; value: { key: string; value: unknown; updatedAt: string } };
  snapshots: { key: string; value: { key: string; value: unknown; createdAt: string } };
  secrets: {
    key: string;
    value: { key: string; identity: IdentityMaterial; backupConfirmed: boolean };
  };
  queue: { key: string; value: { id: string; item: SyncQueueItem } };
  meta: { key: string; value: MetaRecord };
}

/** Lo store degli snapshot dentro la transazione di `saveDocument`. */
type SnapshotStore = IDBPObjectStore<
  PalestraDatabase,
  ("documents" | "snapshots")[],
  "snapshots",
  "readwrite"
>;

let databasePromise: Promise<IDBPDatabase<PalestraDatabase>> | undefined;
let databaseInstance: IDBPDatabase<PalestraDatabase> | undefined;

export function openLocalDatabase(): Promise<IDBPDatabase<PalestraDatabase>> {
  databasePromise ??= openDB<PalestraDatabase>(DATABASE_NAME, DATABASE_VERSION, {
    upgrade(database, oldVersion, _newVersion, transaction) {
      if (oldVersion < 1) {
        database.createObjectStore("documents", { keyPath: "key" });
        database.createObjectStore("secrets", { keyPath: "key" });
        database.createObjectStore("queue", { keyPath: "id" });
        database.createObjectStore("meta", { keyPath: "key" });
      }
      if (oldVersion < 2) {
        database.createObjectStore("snapshots", { keyPath: "key" });
        void transaction.objectStore("meta").put({ key: "schemaVersion", value: 2 });
      }
      if (oldVersion > 0 && oldVersion < 3) {
        /**
         * I documenti scritti fino allo schema 2 sono byte Automerge, e solo
         * Automerge sa rileggerli: tenere la libreria per decodificarli
         * significherebbe tenere anche i 3,38 MB di WASM che questo passaggio
         * elimina. Si scartano.
         *
         * Non si perde l'accesso al profilo: identità, coda e `meta` restano
         * dove sono, e un dispositivo con l'identità ma senza palestra è uno
         * stato che l'app già conosce — l'onboarding offre il ripristino dalle
         * dodici parole, che riscarica la copia cifrata dal relay.
         */
        void transaction.objectStore("documents").clear();
        void transaction.objectStore("snapshots").clear();
      }
      if (oldVersion < 3) {
        void transaction.objectStore("meta").put({ key: "schemaVersion", value: 3 });
      }
    },
    blocking() {
      closeLocalDatabase();
    },
  }).then((database) => {
    databaseInstance = database;
    return database;
  });
  return databasePromise;
}

export function closeLocalDatabase(): void {
  databaseInstance?.close();
  databaseInstance = undefined;
  databasePromise = undefined;
}

function isQuotaError(error: unknown): boolean {
  return error instanceof DOMException && error.name === "QuotaExceededError";
}

export async function loadDocument<T>(key: string): Promise<T | undefined> {
  const database = await openLocalDatabase();
  const record = await database.get("documents", key);
  return record ? (record.value as T) : undefined;
}

/**
 * Quante copie precedenti si tengono per documento. Nessuna funzione le rilegge
 * oggi: sono la sola rete di sicurezza se un salvataggio scrive un documento
 * sbagliato, e per quello ne bastano poche. Senza questo tetto la cronologia
 * cresceva a ogni salvataggio e non veniva mai potata — fino a
 * `QuotaExceededError`, cioè fino a un profilo che smette di poter registrare
 * prenotazioni.
 */
const MAX_SNAPSHOTS_PER_DOCUMENT = 3;

/**
 * La forma che il documento avrà davvero: JSON puro, come viaggia sul filo.
 *
 * Il giro per JSON toglie `undefined`, funzioni e prototipi a tutti i livelli,
 * così ciò che si rilegge è esattamente ciò con cui si è confrontato al
 * salvataggio successivo. Senza, una chiave presente ma indefinita
 * risulterebbe diversa a ogni confronto, cioè una scrittura a ogni tocco.
 */
function documentShape(value: unknown): Record<string, unknown> {
  return JSON.parse(JSON.stringify(value)) as Record<string, unknown>;
}

export async function saveDocument<T>(key: string, value: T): Promise<void> {
  const database = await openLocalDatabase();
  const previous = await database.get("documents", key);
  const next = documentShape(value);
  // Un salvataggio che non cambia nulla non deve costare nulla: né uno
  // snapshot, né una transazione.
  if (previous && sameDocument(previous.value, next)) return;
  const transaction = database.transaction(["documents", "snapshots"], "readwrite");
  try {
    if (previous) {
      const snapshots = transaction.objectStore("snapshots");
      await snapshots.put({
        key: `${key}:${Date.now()}`,
        value: previous.value,
        createdAt: new Date().toISOString(),
      });
      await pruneSnapshots(snapshots, key);
    }
    await transaction
      .objectStore("documents")
      .put({ key, value: next, updatedAt: new Date().toISOString() });
    await transaction.done;
  } catch (error) {
    transaction.abort();
    if (isQuotaError(error))
      throw new Error("Spazio locale esaurito: la modifica non è stata salvata");
    throw error;
  }
}

/**
 * La potatura vive nella stessa transazione della scrittura: un errore dopo la
 * `put` dello snapshot non deve lasciare la finestra più larga di quanto dichiara.
 * Le chiavi sono `${key}:${millisecondi}` e i millisecondi hanno tutti lo stesso
 * numero di cifre fino al 2286: l'ordine alfabetico è quello cronologico, quindi
 * si scarta dalla testa.
 */
async function pruneSnapshots(snapshots: SnapshotStore, key: string): Promise<void> {
  const keys = await snapshots.getAllKeys(IDBKeyRange.bound(`${key}:`, `${key}:￿`));
  const excess = keys.length - MAX_SNAPSHOTS_PER_DOCUMENT;
  for (let index = 0; index < excess; index += 1) await snapshots.delete(keys[index]!);
}

const publicGymKey = (gymId: string) => `public:gym:${gymId}`;
const adminGymKey = (gymId: string) => `admin:gym:${gymId}`;
const personalScheduleKey = (gymId: string, memberId: string) =>
  `personal:${gymId}:schedule:${memberId}`;
const coachProjectionKey = (gymId: string) => `coach:${gymId}:projection`;
const broadcastKey = (gymId: string, broadcastId: string) =>
  `shared:${gymId}:broadcast:${broadcastId}`;
const broadcastEnvelopeKey = (gymId: string, envelopeId: string) =>
  `shared:${gymId}:broadcast-envelope:${envelopeId}`;
const broadcastManifestKey = (gymId: string, broadcastId: string) =>
  `admin:${gymId}:broadcast-manifest:${broadcastId}`;
const broadcastReceiptKey = (gymId: string, broadcastId: string, memberId: string) =>
  `shared:${gymId}:broadcast-receipt:${broadcastId}:${memberId}`;

export async function saveBroadcastDocument(
  message: BroadcastMessage,
  sourceEnvelopeId: string,
  publishedAt?: string,
): Promise<void> {
  await Promise.all([
    saveDocument<StoredBroadcastDocument>(broadcastKey(message.gymId, message.id), {
      message,
      sourceEnvelopeId,
      ...(publishedAt ? { publishedAt } : {}),
    }),
    saveDocument(broadcastEnvelopeKey(message.gymId, sourceEnvelopeId), {
      broadcastId: message.id,
    }),
  ]);
}

export async function markBroadcastEnvelopePublished(
  gymId: string,
  envelopeId: string,
  publishedAt: string,
): Promise<string | undefined> {
  const broadcastId = await broadcastIdForEnvelope(gymId, envelopeId);
  if (!broadcastId) return undefined;
  const key = broadcastKey(gymId, broadcastId);
  const stored = await loadDocument<StoredBroadcastDocument>(key);
  if (!stored || stored.publishedAt) return broadcastId;
  await saveDocument(key, { ...stored, publishedAt });
  return broadcastId;
}

/**
 * Messaggio, manifest, eventuale rimozione e outbox diventano visibili insieme. Se il
 * browser termina a metà non resta una card che l'app chiama «inviata» senza
 * più nulla da spedire al relay.
 */
export async function saveBroadcastOutboxBundle(input: {
  message: BroadcastMessage;
  sourceEnvelopeId: string;
  manifest: BroadcastManifest;
  items: SyncQueueItem[];
  schedule?: MemberSchedule;
}): Promise<void> {
  const database = await openLocalDatabase();
  const transaction = database.transaction(["documents", "queue"], "readwrite");
  try {
    const documents = transaction.objectStore("documents");
    const queue = transaction.objectStore("queue");
    const now = Date.now();
    const records = await queue.getAll();
    let removed = 0;
    for (const record of records) {
      if (expired(record.item, now)) {
        await queue.delete(record.id);
        removed += 1;
      }
    }
    const existingIds = new Set(records.map((record) => record.id));
    const additions = input.items.filter((item) => !existingIds.has(item.envelope.id)).length;
    if (records.length - removed + additions > MAX_QUEUE_ITEMS) throw new SyncQueueFullError();
    const updatedAt = new Date().toISOString();
    if (input.schedule) {
      await documents.put({
        key: personalScheduleKey(input.schedule.gymId, input.schedule.memberId),
        value: documentShape(input.schedule),
        updatedAt,
      });
    }
    await documents.put({
      key: broadcastKey(input.message.gymId, input.message.id),
      value: documentShape({ message: input.message, sourceEnvelopeId: input.sourceEnvelopeId }),
      updatedAt,
    });
    await documents.put({
      key: broadcastEnvelopeKey(input.message.gymId, input.sourceEnvelopeId),
      value: { broadcastId: input.message.id },
      updatedAt,
    });
    await documents.put({
      key: broadcastManifestKey(input.manifest.gymId, input.manifest.broadcastId),
      value: documentShape(input.manifest),
      updatedAt,
    });
    for (const item of input.items) await queue.put({ id: item.envelope.id, item });
    await transaction.done;
  } catch (error) {
    transaction.abort();
    await transaction.done.catch(() => undefined);
    if (isQuotaError(error))
      throw new Error("Spazio locale esaurito: il broadcast non è stato salvato");
    throw error;
  }
}

export async function saveBroadcastManifestDocument(manifest: BroadcastManifest): Promise<void> {
  await saveDocument(broadcastManifestKey(manifest.gymId, manifest.broadcastId), manifest);
}

export async function saveBroadcastReceiptDocument(
  receipt: BroadcastReceipt,
): Promise<BroadcastReceiptProjection> {
  const key = broadcastReceiptKey(receipt.gymId, receipt.broadcastId, receipt.memberId);
  const [previous, broadcast] = await Promise.all([
    loadDocument<BroadcastReceiptProjection>(key),
    loadDocument<StoredBroadcastDocument>(broadcastKey(receipt.gymId, receipt.broadcastId)),
  ]);
  const observedAt = new Date().toISOString();
  const occurredAt = receiptTime(receipt, broadcast, observedAt);
  const next: BroadcastReceiptProjection = {
    gymId: receipt.gymId,
    broadcastId: receipt.broadcastId,
    memberId: receipt.memberId,
    ...(previous?.receivedAt ? { receivedAt: previous.receivedAt } : {}),
    ...(previous?.readAt ? { readAt: previous.readAt } : {}),
    firstObservedAt: previous?.firstObservedAt ?? observedAt,
  };
  if (receipt.state === "received" && !next.receivedAt) next.receivedAt = occurredAt;
  if (receipt.state === "read") {
    next.receivedAt ??= occurredAt;
    next.readAt ??= occurredAt;
  }
  await saveDocument(key, next);
  return next;
}

/** La proiezione monotona e la sua operazione in uscita sono un unico commit. */
export async function saveBroadcastReceiptOutbox(
  receipt: BroadcastReceipt,
  item: SyncQueueItem,
): Promise<BroadcastReceiptProjection> {
  const database = await openLocalDatabase();
  const transaction = database.transaction(["documents", "queue"], "readwrite");
  try {
    const documents = transaction.objectStore("documents");
    const queue = transaction.objectStore("queue");
    const key = broadcastReceiptKey(receipt.gymId, receipt.broadcastId, receipt.memberId);
    const previousRecord = await documents.get(key);
    const broadcastRecord = await documents.get(broadcastKey(receipt.gymId, receipt.broadcastId));
    const previous = previousRecord?.value as BroadcastReceiptProjection | undefined;
    const observedAt = new Date().toISOString();
    const occurredAt = receiptTime(
      receipt,
      broadcastRecord?.value as StoredBroadcastDocument | undefined,
      observedAt,
    );
    const next: BroadcastReceiptProjection = {
      gymId: receipt.gymId,
      broadcastId: receipt.broadcastId,
      memberId: receipt.memberId,
      ...(previous?.receivedAt ? { receivedAt: previous.receivedAt } : {}),
      ...(previous?.readAt ? { readAt: previous.readAt } : {}),
      firstObservedAt: previous?.firstObservedAt ?? observedAt,
    };
    if (receipt.state === "received" && !next.receivedAt) next.receivedAt = occurredAt;
    if (receipt.state === "read") {
      next.receivedAt ??= occurredAt;
      next.readAt ??= occurredAt;
    }
    const records = await queue.getAll();
    let removed = 0;
    for (const record of records) {
      if (record.id !== item.envelope.id && expired(record.item, Date.now())) {
        await queue.delete(record.id);
        removed += 1;
      }
    }
    if (
      !records.some((record) => record.id === item.envelope.id) &&
      records.length - removed >= MAX_QUEUE_ITEMS
    ) {
      throw new SyncQueueFullError();
    }
    await documents.put({ key, value: documentShape(next), updatedAt: new Date().toISOString() });
    await queue.put({ id: item.envelope.id, item });
    await transaction.done;
    return next;
  } catch (error) {
    transaction.abort();
    if (isQuotaError(error))
      throw new Error("Spazio locale esaurito: la ricevuta non è stata salvata");
    throw error;
  }
}

export async function loadBroadcastState(gymId: string): Promise<{
  broadcasts: StoredBroadcastDocument[];
  manifests: BroadcastManifest[];
  receipts: BroadcastReceiptProjection[];
}> {
  const database = await openLocalDatabase();
  const keys = await database.getAllKeys("documents");
  const broadcastPrefix = `shared:${gymId}:broadcast:broadcast_`;
  const manifestPrefix = `admin:${gymId}:broadcast-manifest:`;
  const receiptPrefix = `shared:${gymId}:broadcast-receipt:`;
  const records = await Promise.all(
    keys
      .filter(
        (key) =>
          key.startsWith(broadcastPrefix) ||
          key.startsWith(manifestPrefix) ||
          key.startsWith(receiptPrefix),
      )
      .map((key) => database.get("documents", key)),
  );
  const broadcasts: StoredBroadcastDocument[] = [];
  const manifests: BroadcastManifest[] = [];
  const receipts: BroadcastReceiptProjection[] = [];
  for (const record of records) {
    if (!record) continue;
    if (record.key.startsWith(broadcastPrefix))
      broadcasts.push(record.value as StoredBroadcastDocument);
    else if (record.key.startsWith(manifestPrefix))
      manifests.push(record.value as BroadcastManifest);
    else if (record.key.startsWith(receiptPrefix))
      receipts.push(record.value as BroadcastReceiptProjection);
  }
  broadcasts.sort((left, right) => right.message.createdAt.localeCompare(left.message.createdAt));
  const cutoff = Date.now() - 90 * 24 * 60 * 60 * 1000;
  const retainedBroadcasts = broadcasts
    .filter((entry) => Date.parse(entry.message.createdAt) >= cutoff)
    .slice(0, 100);
  const retainedIds = new Set(retainedBroadcasts.map((entry) => entry.message.id));
  const knownBroadcastIds = new Set(broadcasts.map((entry) => entry.message.id));
  const removedBroadcasts = broadcasts.filter((entry) => !retainedIds.has(entry.message.id));
  if (removedBroadcasts.length > 0) {
    const transaction = database.transaction(["documents", "snapshots"], "readwrite");
    const documents = transaction.objectStore("documents");
    const snapshots = transaction.objectStore("snapshots");
    for (const entry of removedBroadcasts) {
      const keysToDelete = [
        broadcastKey(gymId, entry.message.id),
        broadcastEnvelopeKey(gymId, entry.sourceEnvelopeId),
        broadcastManifestKey(gymId, entry.message.id),
      ];
      for (const receipt of receipts.filter((item) => item.broadcastId === entry.message.id)) {
        keysToDelete.push(broadcastReceiptKey(gymId, receipt.broadcastId, receipt.memberId));
      }
      for (const key of keysToDelete) {
        await documents.delete(key);
        const snapshotKeys = await snapshots.getAllKeys(IDBKeyRange.bound(`${key}:`, `${key}:￿`));
        for (const snapshotKey of snapshotKeys) await snapshots.delete(snapshotKey);
      }
    }
    await transaction.done;
  }
  return {
    broadcasts: retainedBroadcasts,
    manifests: manifests.filter(
      (entry) => retainedIds.has(entry.broadcastId) || !knownBroadcastIds.has(entry.broadcastId),
    ),
    receipts: receipts.filter(
      (entry) => retainedIds.has(entry.broadcastId) || !knownBroadcastIds.has(entry.broadcastId),
    ),
  };
}

export async function broadcastIdForEnvelope(
  gymId: string,
  envelopeId: string,
): Promise<string | undefined> {
  const record = await loadDocument<{ broadcastId: string }>(
    broadcastEnvelopeKey(gymId, envelopeId),
  );
  return record?.broadcastId;
}

export async function saveGymDocuments(gym: Gym): Promise<void> {
  const publicDocument: PublicGymDocument = {
    schemaVersion: gym.schemaVersion,
    id: gym.id,
    name: gym.name,
    timeZone: gym.timeZone,
    slotDurationMinutes: gym.slotDurationMinutes,
  };
  const adminDocument: AdminGymDocument = {
    schemaVersion: gym.schemaVersion,
    gymId: gym.id,
    slots: gym.slots,
    retiredSlots: gym.retiredSlots,
    exceptions: gym.exceptions,
    updatedAt: gym.updatedAt,
    updatedBy: gym.updatedBy,
  };
  await Promise.all([
    saveDocument(publicGymKey(gym.id), publicDocument),
    saveDocument(adminGymKey(gym.id), adminDocument),
  ]);
}

export async function loadGymDocuments(gymId: string): Promise<Gym | undefined> {
  const [publicDocument, adminDocument] = await Promise.all([
    loadDocument<PublicGymDocument>(publicGymKey(gymId)),
    loadDocument<AdminGymDocument>(adminGymKey(gymId)),
  ]);
  if (publicDocument && adminDocument) {
    return {
      schemaVersion: publicDocument.schemaVersion,
      id: publicDocument.id,
      name: publicDocument.name,
      timeZone: publicDocument.timeZone,
      // Lo stesso ripiego dello schema: i documenti nati prima valgono 120.
      slotDurationMinutes: publicDocument.slotDurationMinutes ?? 120,
      slots: [...adminDocument.slots],
      retiredSlots: [...(adminDocument.retiredSlots ?? [])],
      exceptions: [...adminDocument.exceptions],
      updatedAt: adminDocument.updatedAt,
      updatedBy: adminDocument.updatedBy,
    };
  }

  // Compatibilita con il documento unico usato dalle prime build del pilot.
  const legacy = await loadDocument<Gym>(`gym:${gymId}`);
  if (!legacy) return undefined;
  await saveGymDocuments(legacy);
  return legacy;
}

export async function saveMemberScheduleDocument(schedule: MemberSchedule): Promise<void> {
  await saveDocument(personalScheduleKey(schedule.gymId, schedule.memberId), schedule);
}

/**
 * La pianificazione di chi non fa piu' parte della palestra. Se ne vanno anche
 * gli snapshot e la chiave legacy: sono copie degli stessi dati personali, e
 * una revoca che li lasciasse in giro rimuoverebbe la persona ma non la sua
 * storia.
 */
export async function removeMemberScheduleDocument(gymId: string, memberId: string): Promise<void> {
  const database = await openLocalDatabase();
  const keys = [personalScheduleKey(gymId, memberId), `schedule:${memberId}`];
  const transaction = database.transaction(["documents", "snapshots"], "readwrite");
  const documents = transaction.objectStore("documents");
  const snapshots = transaction.objectStore("snapshots");
  for (const key of keys) {
    await documents.delete(key);
    const snapshotKeys = await snapshots.getAllKeys(IDBKeyRange.bound(`${key}:`, `${key}:￿`));
    for (const snapshotKey of snapshotKeys) await snapshots.delete(snapshotKey);
  }
  await transaction.done;
}

export async function loadMemberScheduleDocument(
  gymId: string,
  memberId: string,
): Promise<MemberSchedule | undefined> {
  const document = await loadDocument<MemberSchedule>(personalScheduleKey(gymId, memberId));
  if (document) return document;
  const legacy = await loadDocument<MemberSchedule>(`schedule:${memberId}`);
  if (!legacy) return undefined;
  await saveMemberScheduleDocument(legacy);
  return legacy;
}

const occupancyKey = (gymId: string) => `shared:${gymId}:occupancy`;

/**
 * Il digest sta in un documento a se': lo scrive lo staff, lo legge anche
 * l'atleta, e non ha nulla a che vedere con la propria pianificazione.
 */
export async function saveOccupancyDocument(digest: OccupancyDigest): Promise<void> {
  await saveDocument(occupancyKey(digest.gymId), digest);
}

export async function loadOccupancyDocument(gymId: string): Promise<OccupancyDigest | undefined> {
  return loadDocument<OccupancyDigest>(occupancyKey(gymId));
}

/**
 * Identificativo del dispositivo, non della persona: due browser della stessa
 * identita' devono poter confermare la lettura separatamente. Non e' un segreto
 * e non entra in nessuna firma; serve solo al relay per tenere due caselle.
 */
export async function ensureDeviceId(): Promise<string> {
  const existing = await getMeta<string>("deviceId");
  if (existing) return existing;
  const created = `device_${crypto.randomUUID()}`;
  await setMeta("deviceId", created);
  return created;
}

export async function saveCoachProjectionDocument(
  document: CoachProjectionDocument,
): Promise<void> {
  await saveDocument(coachProjectionKey(document.gymId), document);
}

export async function loadCoachProjectionDocument(
  gymId: string,
): Promise<CoachProjectionDocument | undefined> {
  return loadDocument<CoachProjectionDocument>(coachProjectionKey(gymId));
}

export async function saveIdentity(
  identity: IdentityMaterial,
  backupConfirmed = false,
): Promise<void> {
  const database = await openLocalDatabase();
  await database.put("secrets", { key: "current", identity, backupConfirmed });
}

export async function loadIdentity() {
  const database = await openLocalDatabase();
  return database.get("secrets", "current");
}

export async function confirmIdentityBackup(): Promise<void> {
  const record = await loadIdentity();
  if (record) await saveIdentity(record.identity, true);
}

/**
 * Accoda, e in cambio dice quanti item ha tolto per fare posto: chi tiene il
 * contatore in memoria non puo' limitarsi a incrementarlo, o dopo una
 * sostituzione mostrerebbe un messaggio in piu' di quanti ne esistano.
 *
 * Tre cose accadono qui, tutte nella stessa transazione — un accodamento che
 * leggesse fuori dalla transazione deciderebbe sulla coda di un istante prima:
 *
 * 1. gli item scaduti se ne vanno: un envelope oltre il proprio `expiresAt` il
 *    relay non lo accetterebbe piu' e non c'e' motivo di continuare a contarlo;
 * 2. la `dedupeKey`, se c'e', toglie di mezzo la richiesta identica gia' in
 *    attesa;
 * 3. il tetto ferma la crescita illimitata, che e' il modo in cui una coda
 *    guasta smetteva di ripararsi da sola.
 */
const expired = (item: SyncQueueItem, now: number) => Date.parse(item.envelope.expiresAt) <= now;

export async function enqueue(item: SyncQueueItem, now = Date.now()): Promise<number> {
  const database = await openLocalDatabase();
  const transaction = database.transaction(["queue", "meta"], "readwrite");
  const store = transaction.objectStore("queue");
  if (item.replacement) {
    const meta = transaction.objectStore("meta");
    const previous = await meta.get("replacementVersion");
    const version = Math.max(now, Number(previous?.value ?? 0) + 1);
    item = { ...item, replacement: { ...item.replacement, version } };
    await meta.put({ key: "replacementVersion", value: version });
  }
  const records = await store.getAll();
  let removed = 0;
  for (const record of records) {
    // L'item che si sta riaccodando non e' un duplicato di se stesso: la `put`
    // qui sotto lo sovrascrive, e toglierlo prima sarebbe solo un giro in piu'.
    if (record.id === item.envelope.id) continue;
    const duplicate =
      (item.replacement !== undefined && record.item.replacement?.key === item.replacement.key) ||
      (item.dedupeKey !== undefined && record.item.dedupeKey === item.dedupeKey);
    if (!duplicate && !expired(record.item, now)) continue;
    await store.delete(record.id);
    removed += 1;
  }
  if (records.length - removed >= MAX_QUEUE_ITEMS) {
    // La potatura resta comunque dovuta: annullarla lascerebbe in coda item che
    // nessuno spedira' mai, cioe' renderebbe il tetto definitivo.
    await transaction.done;
    throw new SyncQueueFullError();
  }
  await store.put({ id: item.envelope.id, item });
  await transaction.done;
  return removed;
}

/**
 * La coda viva. Gli envelope scaduti non si contano e non si tengono: restavano
 * in attesa oltre la propria fine, gonfiavano «Messaggi da inviare» e a ogni
 * riconnessione tornavano al relay, che li rifiutava.
 */
export async function queuedItems(now = Date.now()): Promise<SyncQueueItem[]> {
  const database = await openLocalDatabase();
  const records = await database.getAll("queue");
  const dead = records.filter((record) => expired(record.item, now));
  // Solo se c'e' davvero qualcosa da togliere: questa lettura sta sul percorso
  // di ogni `flush` e di ogni riconciliazione, e non deve diventare una
  // scrittura per ciascuno.
  if (dead.length > 0) {
    const transaction = database.transaction("queue", "readwrite");
    for (const record of dead) await transaction.objectStore("queue").delete(record.id);
    await transaction.done;
  }
  return records.filter((record) => !expired(record.item, now)).map((record) => record.item);
}

/**
 * Registra un tentativo di invio: senza, `nextAttemptAt` resta quello dello
 * accodamento e una riconnessione ravvicinata rispedisce l'intera coda.
 */
export async function markAttempt(envelopeId: string, now = Date.now()): Promise<boolean> {
  const database = await openLocalDatabase();
  // Lettura e scrittura nella stessa transazione: separate, una conferma che
  // rimuove l'item tra le due lo farebbe riapparire in coda.
  const transaction = database.transaction("queue", "readwrite");
  const store = transaction.objectStore("queue");
  const record = await store.get(envelopeId);
  const due = Boolean(record && Date.parse(record.item.nextAttemptAt) <= now);
  if (record && due) {
    const attempts = record.item.attempts + 1;
    await store.put({
      id: envelopeId,
      item: { ...record.item, attempts, nextAttemptAt: nextBackoff(attempts, now) },
    });
  }
  await transaction.done;
  return due;
}

export async function acknowledge(envelopeId: string): Promise<void> {
  const database = await openLocalDatabase();
  await database.delete("queue", envelopeId);
}

/**
 * Quanti envelope gia' elaborati si tengono a mente. Non e' una difesa
 * crittografica: e' la memoria che impedisce di rifare — e di far fallire di
 * nuovo — cio' che il relay riconsegna dopo una conferma andata perduta. Cinque
 * centinaia coprono con abbondanza la giacenza di una palestra.
 */
const MAX_HANDLED_ENVELOPES = 500;

const HANDLED_ENVELOPES_KEY = "handledEnvelopes";

export async function wasEnvelopeHandled(envelopeId: string): Promise<boolean> {
  const handled = (await getMeta<string[]>(HANDLED_ENVELOPES_KEY)) ?? [];
  return handled.includes(envelopeId);
}

/**
 * Lettura e scrittura nella stessa transazione: due envelope elaborati nello
 * stesso istante si sovrascriverebbero l'elenco a vicenda.
 */
export async function markEnvelopeHandled(envelopeId: string): Promise<void> {
  const database = await openLocalDatabase();
  const transaction = database.transaction("meta", "readwrite");
  const store = transaction.objectStore("meta");
  const record = await store.get(HANDLED_ENVELOPES_KEY);
  const previous = Array.isArray(record?.value) ? (record.value as string[]) : [];
  if (!previous.includes(envelopeId)) {
    // I piu' recenti in coda: si scarta dalla testa, cioe' il piu' vecchio.
    const next = [...previous, envelopeId].slice(-MAX_HANDLED_ENVELOPES);
    await store.put({ key: HANDLED_ENVELOPES_KEY, value: next });
  }
  await transaction.done;
}

export async function setMeta(key: string, value: unknown): Promise<void> {
  const database = await openLocalDatabase();
  await database.put("meta", { key, value });
}

export async function getMeta<T>(key: string): Promise<T | undefined> {
  const database = await openLocalDatabase();
  return (await database.get("meta", key))?.value as T | undefined;
}

export async function listDocumentKeys(): Promise<string[]> {
  const database = await openLocalDatabase();
  return database.getAllKeys("documents");
}

export async function resetLocalDatabaseForTests(): Promise<void> {
  closeLocalDatabase();
  await deleteLocalProfileData();
}

export async function deleteLocalProfileData(): Promise<void> {
  closeLocalDatabase();
  await new Promise<void>((resolve, reject) => {
    const request = indexedDB.deleteDatabase(DATABASE_NAME);
    request.onsuccess = () => resolve();
    request.onblocked = () =>
      reject(new Error("Chiudi le altre schede dell’app e riprova la rimozione"));
    request.onerror = () =>
      reject(request.error ?? new Error("Impossibile eliminare il database locale"));
  });
}

interface RecipientStatusItem {
  status: RecipientStatus;
  confirmed: boolean;
  attempts: number;
  nextAttemptAt: number;
}
const RECIPIENT_STATUSES = "relayRecipientStatuses";

/** Ultima decisione per identità: una vecchia copia del profilo non la annulla. */
export async function queueRecipientStatus(status: RecipientStatus): Promise<void> {
  const db = await openLocalDatabase();
  const tx = db.transaction("meta", "readwrite");
  const items = ((await tx.store.get(RECIPIENT_STATUSES))?.value ?? []) as RecipientStatusItem[];
  const old = items.find(
    (item) =>
      item.status.channelId === status.channelId && item.status.subjectId === status.subjectId,
  );
  const changed = Date.parse(status.changedAt);
  const previous = old ? Date.parse(old.status.changedAt) : -Infinity;
  if (changed < previous || (changed === previous && (!old!.status.active || status.active))) {
    await tx.done;
    return;
  }
  const next = items.filter((item) => item !== old);
  next.push({ status, confirmed: false, attempts: 0, nextAttemptAt: 0 });
  await tx.store.put({ key: RECIPIENT_STATUSES, value: next });
  await tx.done;
}

export async function pendingRecipientStatuses(channelId: string): Promise<RecipientStatusItem[]> {
  return ((await getMeta<RecipientStatusItem[]>(RECIPIENT_STATUSES)) ?? []).filter(
    (item) => item.status.channelId === channelId && !item.confirmed,
  );
}

export async function updateRecipientStatus(
  status: RecipientStatus,
  confirmed: boolean,
): Promise<void> {
  const db = await openLocalDatabase();
  const tx = db.transaction("meta", "readwrite");
  const items = ((await tx.store.get(RECIPIENT_STATUSES))?.value ?? []) as RecipientStatusItem[];
  const item = items.find((entry) => entry.status.signature === status.signature);
  if (item && !item.confirmed) {
    item.confirmed = confirmed;
    if (!confirmed) {
      item.attempts += 1;
      item.nextAttemptAt = Date.parse(nextBackoff(item.attempts));
    }
    await tx.store.put({ key: RECIPIENT_STATUSES, value: items });
  }
  await tx.done;
}
